import type { PostHog } from 'posthog-js'
import type { WatchBuildResult } from '../api/client'
import { isCompleteCompatibleBuild, type StudioPhase } from '../studioState'

export const AnalyticsEvents = {
  pageview: '$pageview',
  chatMessageSent: 'chat_message_sent',
  chatTurnCompleted: 'chat_turn_completed',
  chatTurnFailed: 'chat_turn_failed',
  suggestionClicked: 'suggestion_clicked',
  assembledImageRequested: 'assembled_image_requested',
  assembledImageCompleted: 'assembled_image_completed',
  assembledImageCacheHit: 'assembled_image_cache_hit',
  assembledImageFailed: 'assembled_image_failed',
  purchaseLinkClicked: 'purchase_link_clicked',
  buyFullBuildClicked: 'buy_full_build_clicked',
  studioStarted: 'studio_started',
  studioPhaseCompleted: 'studio_phase_completed',
  studioBuildViewed: 'studio_build_viewed',
  studioViewChanged: 'studio_view_changed',
  studioRefinementStarted: 'studio_refinement_started',
  studioRetryRequested: 'studio_retry_requested',
  studioBuildReady: 'studio_build_ready',
  studioBuildRestored: 'studio_build_restored',
  studioBuildLinkCopied: 'studio_build_link_copied',
  studioNewBuildStarted: 'studio_new_build_started',
} as const

export type SuggestionSource = 'hero_occasion' | 'build_summary' | 'refinement'
export type PurchaseLinkSource = 'part_card' | 'build_summary' | 'buy_full_build'

function capture(posthog: PostHog | null | undefined, event: string, properties?: Record<string, unknown>) {
  if (!posthog) return
  posthog.capture(event, properties)
}

export function classifyErrorKind(message: string): string {
  if (message.includes('No compatible')) return 'no_compatible_parts'
  if (message.toLowerCase().includes('network') || message.toLowerCase().includes('fetch')) {
    return 'network'
  }
  if (message.toLowerCase().includes('timeout')) return 'timeout'
  return 'other'
}

export function buildTurnMetrics(buildResult: WatchBuildResult | null | undefined) {
  if (!buildResult) {
    return {
      has_build: false,
      part_count: 0,
      failed_step_count: 0,
      has_compatibility_errors: false,
    }
  }

  return {
    has_build: Object.keys(buildResult.build.parts).length > 0,
    part_count: Object.keys(buildResult.build.parts).length,
    failed_step_count: buildResult.failed_steps.length,
    has_compatibility_errors: buildResult.compatibility_errors.length > 0,
  }
}

export function registerConversation(posthog: PostHog | null | undefined, conversationId: string) {
  if (!posthog) return
  posthog.register({ conversation_id: conversationId })
}

export function captureChatMessageSent(
  posthog: PostHog | null | undefined,
  hasConversationId: boolean,
) {
  capture(posthog, AnalyticsEvents.chatMessageSent, { has_conversation_id: hasConversationId })
}

export function captureChatTurnCompleted(
  posthog: PostHog | null | undefined,
  buildResult: WatchBuildResult | null | undefined,
) {
  capture(posthog, AnalyticsEvents.chatTurnCompleted, buildTurnMetrics(buildResult))
}

export function captureChatTurnFailed(posthog: PostHog | null | undefined, errorMessage: string) {
  capture(posthog, AnalyticsEvents.chatTurnFailed, {
    error_kind: classifyErrorKind(errorMessage),
  })
}

export function captureSuggestionClicked(
  posthog: PostHog | null | undefined,
  source: SuggestionSource,
) {
  capture(posthog, AnalyticsEvents.suggestionClicked, { source })
}

export function captureAssembledImageRequested(
  posthog: PostHog | null | undefined,
  partCount: number,
) {
  capture(posthog, AnalyticsEvents.assembledImageRequested, { part_count: partCount })
}

export function captureAssembledImageCompleted(
  posthog: PostHog | null | undefined,
  cached: boolean,
) {
  capture(posthog, AnalyticsEvents.assembledImageCompleted, { cached })
}

export function captureAssembledImageCacheHit(
  posthog: PostHog | null | undefined,
  buildFingerprint: string,
) {
  capture(posthog, AnalyticsEvents.assembledImageCacheHit, { build_fingerprint: buildFingerprint })
}

export function captureAssembledImageFailed(posthog: PostHog | null | undefined, errorMessage: string) {
  capture(posthog, AnalyticsEvents.assembledImageFailed, {
    error_kind: classifyErrorKind(errorMessage),
  })
}

export function capturePurchaseLinkClicked(
  posthog: PostHog | null | undefined,
  properties: {
    click_type: 'part' | 'build_cart'
    source: PurchaseLinkSource
    part_id?: string
    provider_id: string
    part_type?: string
    part_count_in_build: number
    has_conversation_id: boolean
  },
) {
  capture(posthog, AnalyticsEvents.purchaseLinkClicked, properties)
}

export function captureBuyFullBuildClicked(
  posthog: PostHog | null | undefined,
  properties: {
    provider_id: string
    part_count: number
    total_price: number
    has_conversation_id: boolean
  },
) {
  capture(posthog, AnalyticsEvents.buyFullBuildClicked, properties)
}

export function captureStudioStarted(
  posthog: PostHog | null | undefined,
  hasExistingBuild: boolean,
) {
  capture(posthog, AnalyticsEvents.studioStarted, { has_existing_build: hasExistingBuild })
}

export function captureStudioPhaseCompleted(
  posthog: PostHog | null | undefined,
  phase: StudioPhase,
  durationMs: number,
) {
  capture(posthog, AnalyticsEvents.studioPhaseCompleted, {
    phase,
    duration_ms: Math.max(0, Math.round(durationMs)),
  })
}

export function captureStudioBuildViewed(
  posthog: PostHog | null | undefined,
  buildResult: WatchBuildResult | null,
  hadUnreadBadge: boolean,
) {
  capture(posthog, AnalyticsEvents.studioBuildViewed, {
    ...buildTurnMetrics(buildResult),
    had_unread_badge: hadUnreadBadge,
  })
}

export function captureStudioViewChanged(
  posthog: PostHog | null | undefined,
  view: 'conversation' | 'build',
) {
  capture(posthog, AnalyticsEvents.studioViewChanged, { view })
}

export function captureStudioRefinementStarted(
  posthog: PostHog | null | undefined,
  source: 'composer' | 'suggestion' | 'build_action',
) {
  capture(posthog, AnalyticsEvents.studioRefinementStarted, { source })
}

export function captureStudioRetryRequested(posthog: PostHog | null | undefined) {
  capture(posthog, AnalyticsEvents.studioRetryRequested)
}

export function captureStudioBuildReady(
  posthog: PostHog | null | undefined,
  buildResult: WatchBuildResult,
) {
  if (!isCompleteCompatibleBuild(buildResult)) return
  capture(posthog, AnalyticsEvents.studioBuildReady, buildTurnMetrics(buildResult))
}

export function captureStudioBuildRestored(
  posthog: PostHog | null | undefined,
  buildResult: WatchBuildResult | null,
  source: 'url' | 'local_storage',
) {
  capture(posthog, AnalyticsEvents.studioBuildRestored, {
    ...buildTurnMetrics(buildResult),
    source,
  })
}

export function captureStudioBuildLinkCopied(
  posthog: PostHog | null | undefined,
  persisted: boolean,
) {
  capture(posthog, AnalyticsEvents.studioBuildLinkCopied, { persisted })
}

export function captureStudioNewBuildStarted(posthog: PostHog | null | undefined) {
  capture(posthog, AnalyticsEvents.studioNewBuildStarted)
}
